import { useState } from "react";
import { api, fmt } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "kelly", label: "KELLY SIZING", tid: "tab-kelly" },
  { id: "position", label: "POSITION SIZE", tid: "tab-position" },
  { id: "risk-parity", label: "RISK PARITY", tid: "tab-risk-parity" },
  { id: "var", label: "VAR / CVAR", tid: "tab-var" },
];

export default function Portfolio() {
  const [tab, setTab] = useState("kelly");

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="portfolio-page">
      <div className="border-b border-[color:var(--border-default)] pb-4 mb-6">
        <div className="text-[10px] font-mono-data text-[#FFD700] tracking-widest mb-2">◆ PORTFOLIO OPTIMIZER · KELLY · RISK PARITY · VAR</div>
        <h1 className="font-serif-elite text-4xl md:text-5xl font-bold tracking-tight">Portfolio Lab</h1>
      </div>

      <div className="flex gap-0 border border-[color:var(--border-default)] mb-4 w-fit" data-testid="portfolio-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={t.tid}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[11px] font-mono-data tracking-widest border-r border-[color:var(--border-default)] last:border-r-0 transition-colors ${
              tab === t.id ? "bg-white text-black" : "text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "kelly" && <KellyPanel />}
      {tab === "position" && <PositionPanel />}
      {tab === "risk-parity" && <RiskParityPanel />}
      {tab === "var" && <VarPanel />}
    </div>
  );
}

function KellyPanel() {
  const [wp, setWp] = useState(0.55);
  const [aw, setAw] = useState(2.0);
  const [al, setAl] = useState(1.0);
  const [frac, setFrac] = useState(0.5);
  const [acct, setAcct] = useState(100000);
  const [r, setR] = useState(null);
  const compute = async () => {
    try {
      const { data } = await api.post("/portfolio/kelly", {
        win_prob: parseFloat(wp), avg_win_pct: parseFloat(aw), avg_loss_pct: parseFloat(al),
        fractional: parseFloat(frac), account_size: parseFloat(acct),
      });
      setR(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Kelly calc failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="kelly-panel">
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
        <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">INPUTS</div>
        <Field label="WIN PROBABILITY (0-1)" value={wp} onChange={setWp} tid="kelly-wp" />
        <Field label="AVG WIN %" value={aw} onChange={setAw} tid="kelly-aw" />
        <Field label="AVG LOSS %" value={al} onChange={setAl} tid="kelly-al" />
        <Field label="FRACTIONAL KELLY (0-1, 0.5 = HALF-KELLY)" value={frac} onChange={setFrac} tid="kelly-frac" />
        <Field label="ACCOUNT SIZE ($)" value={acct} onChange={setAcct} tid="kelly-acct" />
        <button data-testid="kelly-compute-btn" onClick={compute} className="w-full mt-3 bg-white text-black py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90">COMPUTE</button>
      </div>
      {r && (
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid="kelly-result">
          <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">RECOMMENDATION</div>
          <KV label="KELLY FRACTION" value={fmt(r.kelly_fraction)} />
          <KV label="FRACTIONAL KELLY" value={fmt(r.fractional_kelly)} tone={r.fractional_kelly > 0 ? "bull" : "bear"} />
          <KV label="RECOMMENDED SIZE %" value={`${r.recommended_size_pct}%`} tone="gold" />
          <KV label="RECOMMENDED $" value={`$${fmt(r.recommended_dollars)}`} tone="gold" />
          <KV label="EDGE (E[R] / TRADE)" value={`${r.expected_return_per_trade_pct}%`} tone={r.expected_return_per_trade_pct > 0 ? "bull" : "bear"} />
          <KV label="PAYOFF RATIO" value={r.payoff_ratio} />
          <div className="text-[10px] font-body text-[color:var(--text-secondary)] mt-3 leading-relaxed">{r.note}</div>
        </div>
      )}
    </div>
  );
}

function PositionPanel() {
  const [acct, setAcct] = useState(100000);
  const [risk, setRisk] = useState(1.0);
  const [entry, setEntry] = useState(100);
  const [stop, setStop] = useState(95);
  const [r, setR] = useState(null);
  const compute = async () => {
    try {
      const { data } = await api.post("/portfolio/position-sizing", {
        account_size: parseFloat(acct), risk_per_trade_pct: parseFloat(risk),
        entry_price: parseFloat(entry), stop_price: parseFloat(stop),
      });
      setR(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Position sizing failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="position-panel">
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
        <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">INPUTS</div>
        <Field label="ACCOUNT SIZE ($)" value={acct} onChange={setAcct} tid="pos-acct" />
        <Field label="RISK PER TRADE %" value={risk} onChange={setRisk} tid="pos-risk" />
        <Field label="ENTRY PRICE" value={entry} onChange={setEntry} tid="pos-entry" />
        <Field label="STOP PRICE" value={stop} onChange={setStop} tid="pos-stop" />
        <button data-testid="pos-compute-btn" onClick={compute} className="w-full mt-3 bg-white text-black py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90">COMPUTE</button>
      </div>
      {r && (
        <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4" data-testid="position-result">
          <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">SIZING</div>
          <KV label="SHARES" value={r.shares} tone="gold" />
          <KV label="POSITION $" value={`$${fmt(r.position_value)}`} />
          <KV label="RISK $" value={`$${fmt(r.risk_amount)}`} tone="bear" />
          <KV label="POSITION % OF ACCOUNT" value={`${r.position_size_pct}%`} />
          <KV label="LEVERAGE" value={`${r.leverage}×`} />
        </div>
      )}
    </div>
  );
}

function RiskParityPanel() {
  const [sym, setSym] = useState("SPY,QQQ,GLD,TLT,BTC-USD");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const compute = async () => {
    setBusy(true);
    try {
      const symbols = sym.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      const { data } = await api.post("/portfolio/risk-parity", { symbols, period: "1y" });
      setR(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="riskparity-panel">
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
        <div className="text-[10px] font-mono-data text-white tracking-widest mb-2">SYMBOLS (comma-separated, 2-15)</div>
        <div className="flex gap-2">
          <input data-testid="rp-symbols" value={sym} onChange={(e) => setSym(e.target.value)} className="flex-1 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-2 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
          <button data-testid="rp-compute-btn" onClick={compute} disabled={busy} className="bg-white text-black px-4 text-[11px] font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50">{busy ? "…" : "OPTIMIZE"}</button>
        </div>
      </div>
      {r && (
        <div className="space-y-4" data-testid="rp-result">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border border-[color:var(--border-default)]">
            <Cell label="PORT VOL (ANN)" value={`${(r.portfolio_ann_vol * 100).toFixed(2)}%`} tone="bear" />
            <Cell label="PORT RETURN (ANN)" value={`${(r.portfolio_ann_return * 100).toFixed(2)}%`} tone="bull" />
            <Cell label="SHARPE" value={r.portfolio_sharpe} tone="gold" />
          </div>
          <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4">
            <div className="text-[10px] font-mono-data text-white tracking-widest mb-3">RISK-PARITY WEIGHTS</div>
            {Object.entries(r.weights).map(([s, w]) => (
              <div key={s} className="flex items-center gap-2 py-1">
                <span className="w-20 text-xs font-mono-data text-white">{s}</span>
                <div className="flex-1 h-4 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)]">
                  <div className="h-full bg-gold" style={{ width: `${w * 100}%` }} />
                </div>
                <span className="w-16 text-right text-xs font-mono-data text-gold">{(w * 100).toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VarPanel() {
  const [sym, setSym] = useState("SPY,QQQ,GLD");
  const [pv, setPv] = useState(100000);
  const [horizon, setHorizon] = useState(1);
  const [conf, setConf] = useState(0.95);
  const [r, setR] = useState(null);
  const compute = async () => {
    try {
      const symbols = sym.split(",").map(s => s.trim().toUpperCase()).filter(Boolean);
      const { data } = await api.post("/portfolio/var", {
        symbols, portfolio_value: parseFloat(pv), horizon_days: parseInt(horizon),
        confidence: parseFloat(conf), period: "1y",
      });
      setR(data);
    } catch (e) { toast.error(e.response?.data?.detail || "VaR calc failed"); }
  };
  return (
    <div className="space-y-4" data-testid="var-panel">
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="SYMBOLS" value={sym} onChange={setSym} tid="var-sym" text />
        <Field label="PORTFOLIO $" value={pv} onChange={setPv} tid="var-pv" />
        <Field label="HORIZON (DAYS)" value={horizon} onChange={setHorizon} tid="var-horizon" />
        <Field label="CONFIDENCE (0.5-0.999)" value={conf} onChange={setConf} tid="var-conf" />
        <button data-testid="var-compute-btn" onClick={compute} className="md:col-span-4 bg-white text-black py-2 text-[11px] font-mono-data tracking-widest hover:opacity-90">COMPUTE</button>
      </div>
      {r && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border-default)]" data-testid="var-result">
          <Cell label={`VAR ${(r.confidence * 100).toFixed(0)}%`} value={`${r.var_pct}%`} tone="bear" />
          <Cell label="VAR $" value={`$${fmt(r.var_dollar)}`} tone="bear" />
          <Cell label={`CVAR (EXPECTED SHORTFALL)`} value={`${r.cvar_pct}%`} tone="bear" />
          <Cell label="CVAR $" value={`$${fmt(r.cvar_dollar)}`} tone="bear" />
          <Cell label="ANN VOL" value={`${r.annualized_vol_pct}%`} />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, tid, text }) {
  return (
    <label className="block mb-2">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <input data-testid={tid} type={text ? "text" : "number"} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-2 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)] mt-1" />
    </label>
  );
}

function KV({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="flex items-center justify-between py-1 border-b border-[color:var(--border-default)] last:border-0">
      <span className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">{label}</span>
      <span className={`text-sm font-mono-data ${c}`}>{value}</span>
    </div>
  );
}

function Cell({ label, value, tone }) {
  const c = tone === "bull" ? "text-bull" : tone === "bear" ? "text-bear" : tone === "gold" ? "text-gold" : "text-white";
  return (
    <div className="p-3 border-r border-b border-[color:var(--border-default)]">
      <div className="text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">{label}</div>
      <div className={`font-mono-data text-lg mt-1 ${c}`}>{value}</div>
    </div>
  );
}
